import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { corporateInput, EmptyState, fmtMoney, fmtDate } from "@/components/Shared";
import { MiniStat } from "@/components/DashboardWidgets";
import {
  HandCoins, Plus, CheckCircle, Clock, XCircle, Calendar,
  CurrencyDollar, Users as UsersIcon, TrendUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  pending:   { label: "Pendiente",        color: "bg-slate-100 text-slate-700 border-slate-200" },
  approved:  { label: "Aprobada",         color: "bg-blue-50 text-blue-700 border-blue-200" },
  scheduled: { label: "Programada",       color: "bg-amber-50 text-amber-700 border-amber-200" },
  paid:      { label: "Pagada",           color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelada",        color: "bg-red-50 text-red-700 border-red-200" },
};

const FILTERS = [
  { v: "all", label: "Todas" },
  { v: "pending", label: "Pendientes" },
  { v: "approved", label: "Aprobadas" },
  { v: "scheduled", label: "Programadas" },
  { v: "paid", label: "Pagadas" },
  { v: "cancelled", label: "Canceladas" },
];

export default function PayoutsPage() {
  const [filter, setFilter] = useState("all");
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ referrer_client_id: "", referred_client_id: "", plan_id: "", commission_amount: 0, notes: "" });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const [l, s] = await Promise.all([
      api.get("/referrals", { params: { status: filter === "all" ? undefined : filter } }),
      api.get("/referrals/summary"),
    ]);
    setList(l.data); setSummary(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data)).catch(() => {});
    // plans live in support module — only available if the financial user happens to have access OR via shared cache.
    // To keep this admin-friendly we attempt and ignore on 403.
    api.get("/support/plans").then(r => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const save = async () => {
    try {
      await api.post("/referrals", {
        ...form,
        commission_amount: Number(form.commission_amount || 0),
        plan_id: form.plan_id || null,
      });
      toast.success("Referido registrado");
      setOpen(false);
      setForm({ referrer_client_id: "", referred_client_id: "", plan_id: "", commission_amount: 0, notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (id, status, payout_date = null, payout_reference = null) => {
    setBusyId(id);
    try {
      await api.patch(`/referrals/${id}/status`, { status, payout_date, payout_reference });
      toast.success(`Estado: ${STATUS_META[status].label}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este referido?")) return;
    try { await api.delete(`/referrals/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PageShell
      section="Finanzas"
      page="Pagos por referido"
      context={summary ? `${summary.active_referrers} referidores activos` : ""}
      testid="payouts-toolbar"
      actions={<Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="add-referral"><Plus size={15} className="mr-1.5" /> Registrar referido</Button>}
    >
      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MiniStat testid="po-generated" label="Comisiones generadas" tone="indigo" icon={TrendUp} value={Math.round(summary?.generated_total || 0)} hint={summary ? `$${(summary.generated_total).toFixed(2)} total` : ""} />
        <MiniStat testid="po-pending" label="Pendientes de pago" tone="amber" icon={Clock} value={Math.round(summary?.pending_total || 0)} hint={summary ? `$${(summary.pending_total).toFixed(2)} debe`: ""} />
        <MiniStat testid="po-paid" label="Pagadas" tone="emerald" icon={CheckCircle} value={Math.round(summary?.paid_total || 0)} hint={summary ? `$${(summary.paid_total).toFixed(2)} pagado` : ""} />
        <MiniStat testid="po-balance" label="Balance pendiente" tone="red" icon={CurrencyDollar} value={Math.round(summary?.balance_due || 0)} hint="por liquidar" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MiniStat testid="po-active" label="Referidores activos" tone="blue" icon={UsersIcon} value={summary?.active_referrers} />
        <MiniStat testid="po-paid-refs" label="Referidores pagados" tone="emerald" icon={CheckCircle} value={summary?.paid_referrers} />
      </div>

      {/* Filter tabs */}
      <div className="mb-4 flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1 w-fit">
        {FILTERS.map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`po-filter-${f.v}`}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${filter === f.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Referidor</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Referido</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Plan</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Activación</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 text-right">Comisión</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Estado</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">Fecha pago</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 && (
              <tr><td colSpan={8}><EmptyState icon={HandCoins} title="Sin referidos" message="Registra tu primer referido para empezar a generar comisiones." /></td></tr>
            )}
            {list.map(r => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`referral-row-${r.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{r.referrer_name || "—"}</td>
                <td className="px-5 py-3 text-slate-700">{r.referred_name || "—"}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{r.plan_name || "—"}</td>
                <td className="px-5 py-3 text-slate-600 text-xs font-mono">{fmtDate(r.activated_at)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-slate-900">{fmtMoney(r.commission_amount)}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_META[r.status]?.color}`}>
                    {STATUS_META[r.status]?.label || r.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-slate-600 text-xs font-mono">{r.paid_at ? fmtDate(r.paid_at) : r.payout_date ? <span className="text-amber-700">{fmtDate(r.payout_date)} (prog.)</span> : "—"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-0.5">
                    {r.status === "pending" && (
                      <button onClick={() => setStatus(r.id, "approved")} disabled={busyId === r.id} className="text-blue-600 hover:bg-blue-50 px-2 py-1 rounded text-xs font-semibold" data-testid={`r-approve-${r.id}`}>Aprobar</button>
                    )}
                    {r.status === "approved" && (
                      <button onClick={() => {
                        const d = prompt("Fecha de pago programada (YYYY-MM-DD):");
                        if (d) setStatus(r.id, "scheduled", d);
                      }} disabled={busyId === r.id} className="text-amber-600 hover:bg-amber-50 px-2 py-1 rounded text-xs font-semibold" data-testid={`r-schedule-${r.id}`}>Programar</button>
                    )}
                    {["approved", "scheduled"].includes(r.status) && (
                      <button onClick={() => {
                        const ref = prompt("Referencia/comprobante del pago (opcional):") || "";
                        setStatus(r.id, "paid", null, ref);
                      }} disabled={busyId === r.id} className="text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded text-xs font-semibold" data-testid={`r-pay-${r.id}`}>Marcar pagado</button>
                    )}
                    {!["paid", "cancelled"].includes(r.status) && (
                      <button onClick={() => setStatus(r.id, "cancelled")} disabled={busyId === r.id} className="text-slate-500 hover:bg-red-50 hover:text-red-600 px-2 py-1 rounded text-xs font-semibold" data-testid={`r-cancel-${r.id}`}>Cancelar</button>
                    )}
                    <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded" title="Eliminar"><XCircle size={14} weight="bold" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Registrar nuevo referido</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente referidor *</Label>
              <select value={form.referrer_client_id} onChange={(e) => setForm({...form, referrer_client_id: e.target.value})} className={corporateInput} data-testid="referrer-select">
                <option value="">Selecciona…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente referido *</Label>
              <select value={form.referred_client_id} onChange={(e) => setForm({...form, referred_client_id: e.target.value})} className={corporateInput} data-testid="referred-select">
                <option value="">Selecciona…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Plan (opcional)</Label>
                <select value={form.plan_id} onChange={(e) => setForm({...form, plan_id: e.target.value})} className={corporateInput}>
                  <option value="">—</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Comisión ($) *</Label>
                <Input type="number" step="0.01" value={form.commission_amount} onChange={(e) => setForm({...form, commission_amount: e.target.value})} className={corporateInput} data-testid="commission-input" />
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!form.referrer_client_id || !form.referred_client_id || !form.commission_amount} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-referral">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
